// src/components/TeamManager.jsx
import React, { useEffect, useState, useRef } from "react";
import { db } from "../firebase-config";
import {
  collection,
  addDoc,
  getDocs,
  updateDoc,
  doc,
} from "firebase/firestore";
import CustomEditor from "../Components/CustomTexteditor";
import GoogleDriveImage from "./GoogleDriveImage";

const initialForm = {
  name: "",
  role: "",
  img: "",
  bio: "",
  bigParagraph: "",
  facebook: "",
  twitter: "",
  linkedin: "",
  instagram: "",
  photos: "",
  youtube: "",
  cNo: "",
  rNo: "",
};

function TeamManager() {
  const [team, setTeam] = useState([]);
  const [form, setForm] = useState(initialForm);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);

  // NEW: image source selectors
  const [profileSource, setProfileSource] = useState("cloudinary");
  const [gallerySource, setGallerySource] = useState("cloudinary");

  const headingRef = useRef(null);

  useEffect(() => {
    fetchTeam();
  }, []);

  /* ---------------- FETCH TEAM ---------------- */
  const fetchTeam = async () => {
    const snapshot = await getDocs(collection(db, "team"));
    const data = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
    setTeam(data);
  };

  /* ---------------- FORM CHANGE ---------------- */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  /* ---------------- DUPLICATE SAFE ADD ---------------- */
  const addGalleryPhotoOnce = (url) => {
    if (!url) return;

    setForm((prev) => {
      const existing = prev.photos
        ? prev.photos.split(",").map((p) => p.trim())
        : [];

      if (existing.includes(url)) return prev;

      return {
        ...prev,
        photos: existing.length ? [...existing, url].join(", ") : url,
      };
    });
  };

  /* ---------------- CLOUDINARY ---------------- */
  const uploadCloudinary = (cb) => {
    const widget = window.cloudinary.createUploadWidget(
      {
        cloudName: "dgxhp09em",
        uploadPreset: "unsigned_preset",
      },
      (error, result) => {
        if (!error && result?.event === "success") {
          cb(result.info.secure_url);
        }
      }
    );
    widget.open();
  };

  /* ---------------- SUBMIT ---------------- */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const data = {
      name: form.name,
      role: form.role,
      img: form.img,
      bio: form.bio,
      bigParagraph: form.bigParagraph,
      social: {
        facebook: form.facebook,
        twitter: form.twitter,
        linkedin: form.linkedin,
        instagram: form.instagram,
      },
      photos: form.photos
        ? Array.from(new Set(form.photos.split(",").map((p) => p.trim())))
        : [],
      youtube: form.youtube,
      cNo: form.cNo ? Number(form.cNo) : null,
      rNo: form.rNo ? Number(form.rNo) : null,
    };

    if (editingId) {
      await updateDoc(doc(db, "team", editingId), data);
    } else {
      await addDoc(collection(db, "team"), data);
    }

    resetForm();
    fetchTeam();
    setLoading(false);
  };

  /* ---------------- EDIT ---------------- */
  const handleEdit = (m) => {
    setForm({
      name: m.name || "",
      role: m.role || "",
      img: m.img || "",
      bio: m.bio || "",
      bigParagraph: m.bigParagraph || "",
      facebook: m.social?.facebook || "",
      twitter: m.social?.twitter || "",
      linkedin: m.social?.linkedin || "",
      instagram: m.social?.instagram || "",
      photos: m.photos ? m.photos.join(", ") : "",
      youtube: m.youtube || "",
      cNo: m.cNo?.toString() || "",
      rNo: m.rNo?.toString() || "",
    });
    setEditingId(m.id);

    headingRef.current.scrollIntoView({ behavior: "smooth" });
  };

  const resetForm = () => {
    setForm(initialForm);
    setEditingId(null);
    setProfileSource("cloudinary");
    setGallerySource("cloudinary");
  };

  /* ---------------- UI ---------------- */
  return (
    <div className="container my-4">
      <h3 ref={headingRef}>
        {editingId ? "Edit Team Member" : "Add Team Member"}
      </h3>

      <form onSubmit={handleSubmit} className="row g-3">

        <input className="form-control" name="name" value={form.name} onChange={handleChange} placeholder="Name" required />
        <input className="form-control" name="role" value={form.role} onChange={handleChange} placeholder="Role" required />

        {/* -------- PROFILE IMAGE -------- */}
        <div>
          <label>Profile Image</label>
          <select className="form-select mb-2" value={profileSource} onChange={(e) => setProfileSource(e.target.value)}>
            <option value="cloudinary">Cloudinary</option>
            <option value="drive">Google Drive</option>
          </select>

          {profileSource === "drive" ? (
            <GoogleDriveImage onImageReady={(url) => setForm((f) => ({ ...f, img: url }))} />
          ) : (
            <button type="button" className="btn btn-outline-primary" onClick={() =>
              uploadCloudinary((url) => setForm((f) => ({ ...f, img: url })))
            }>
              Upload Profile Image
            </button>
          )}
        </div>

        {/* -------- GALLERY -------- */}
        <div>
          <label>Gallery Photos</label>
          <select className="form-select mb-2" value={gallerySource} onChange={(e) => setGallerySource(e.target.value)}>
            <option value="cloudinary">Cloudinary</option>
            <option value="drive">Google Drive</option>
          </select>

          {gallerySource === "drive" ? (
            <GoogleDriveImage onImageReady={addGalleryPhotoOnce} />
          ) : (
            <button type="button" className="btn btn-outline-primary" onClick={() =>
              uploadCloudinary(addGalleryPhotoOnce)
            }>
              Upload Gallery Photo
            </button>
          )}

          <small className="text-muted d-block mt-1">
            Added: {form.photos || "No images yet"}
          </small>
        </div>

        <button className="btn btn-success mt-3" disabled={loading}>
          {editingId ? "Update" : "Add"}
        </button>
      </form>

      <hr />

      <div className="row">
        {team.map((m) => (
          <div key={m.id} className="col-md-4">
            <div className="card p-3">
              <img src={m.img} alt="" style={{ width: 60, height: 60, borderRadius: "50%" }} />
              <h6>{m.name}</h6>
              <small>{m.role}</small>
              <button className="btn btn-sm btn-outline-primary mt-2" onClick={() => handleEdit(m)}>
                Edit
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default TeamManager;
